/**
 * React Query hooks over the API. Keys are stable arrays so screens can
 * invalidate precisely after a save.
 */
import { useMutation, useQuery, useQueryClient, useInfiniteQuery } from "@tanstack/react-query";
import type { Page } from "@crc/shared";
import { api, qs } from "./api.ts";

export interface RefValueDto {
  valueKey: string;
  label: string;
  labelHe: string | null;
  colour: string | null;
  active: boolean;
  order: number;
  metadata: Record<string, unknown>;
}
export interface RefListDto {
  key: string;
  name: string;
  description: string | null;
  system: boolean;
  values: RefValueDto[];
}

export function useReference() {
  return useQuery({ queryKey: ["reference"], queryFn: () => api.get<{ lists: RefListDto[] }>("/reference"), staleTime: 5 * 60_000 });
}

/** Label lookup for a reference value key; falls back to the key humanised. */
export function useRefLabel() {
  const { data } = useReference();
  return (listKey: string, valueKey: string | null | undefined): string => {
    if (!valueKey) return "";
    const v = data?.lists.find((l) => l.key === listKey)?.values.find((x) => x.valueKey === valueKey);
    return v?.label ?? valueKey.replace(/_/g, " ");
  };
}

export function useRefValues(listKey: string, includeInactive = false): RefValueDto[] {
  const { data } = useReference();
  const vals = data?.lists.find((l) => l.key === listKey)?.values ?? [];
  return includeInactive ? vals : vals.filter((v) => v.active);
}

export interface PersonDto {
  id: string;
  name: string;
  hebrewName: string | null;
  crcCode: string | null;
  roles: string[];
  active: boolean;
  email?: string | null;
  phone?: string | null;
  agencyName?: string | null;
}
export function usePeople(role?: string) {
  return useQuery({ queryKey: ["people", role ?? "all"], queryFn: () => api.get<{ items: PersonDto[] }>(`/people${qs({ role, active: true })}`), staleTime: 5 * 60_000 });
}

export function useCompanyList(params: Record<string, string | number | boolean | undefined>) {
  return useInfiniteQuery({
    queryKey: ["companies", params],
    queryFn: ({ pageParam }) => api.get<Page<CompanyListItem> & { total: number }>(`/companies${qs({ ...params, cursor: pageParam as string | undefined, limit: 50 })}`),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  });
}

export interface CompanyListItem {
  id: string;
  name: string;
  dba: string | null;
  status: string;
  productionType: string;
  restrictionType: string | null;
  kosherForPesach: boolean;
  rcName: string | null;
  rcPersonId: string | null;
  onboardingStage: string | null;
  facilityCount: number;
  activeLocCount: number;
  updatedAt: string;
  parentCompanyId: string | null;
  corporateAddress: { city?: string | null; state?: string | null } | null;
}

export function useCompany(id: string | undefined) {
  return useQuery({ queryKey: ["company", id], queryFn: () => api.get<CompanyDetail>(`/companies/${id}`), enabled: !!id });
}

export interface CompanyDetail extends Record<string, unknown> {
  id: string;
  name: string;
  dba: string | null;
  otherName: string | null;
  status: string;
  productionType: string;
  billingMode: string;
  parentCompanyId: string | null;
  rcPersonId: string | null;
  coRcPersonId: string | null;
  restrictionType: string | null;
  kosherGroup: string | null;
  kosherForPesach: boolean;
  sellChometz: boolean;
  jewishOwnership: string;
  yoshon: string;
  cholovYisroel: boolean;
  pasYisroel: boolean;
  mevushal: boolean;
  fish: boolean;
  kitniyos: boolean;
  certifications: string[];
  certificateWording: string | null;
  adminFee: number | null;
  monthlyFee: number;
  annualFee: number | null;
  productionBilling: boolean;
  contractType: string | null;
  contractStart: string | null;
  contractExpiration: string | null;
  companyOverview: string | null;
  kosherNotes: string | null;
  billingNotes?: string | null;
  directions: string | null;
  website: string | null;
  generalPhone: string | null;
  generalEmail: string | null;
  listingCategory: string | null;
  corporateAddress: AddressDto | null;
  ownerName?: string | null;
  ownerHomeAddress?: AddressDto | null;
  billingSameAsBusiness: boolean;
  billingName: string | null;
  billingAddress: AddressDto | null;
  billingEmail: string | null;
  billingPhone: string | null;
  autoPay: boolean;
  qboCustomerRef?: string | null;
  updatedAt: string;
  createdAt: string;
  customFields: Record<string, unknown>;
  rc: { id: string; name: string } | null;
  coRc: { id: string; name: string } | null;
  parent: { id: string; name: string } | null;
  subAccounts: Array<{ id: string; name: string; status: string; billingMode: string }>;
  onboarding: { stage: string; startedAt: string; stageChangedAt: string; completedAt: string | null } | null;
  primaryContact: ContactDto | null;
  billingContact: ContactDto | null;
  stats: { facilities: number; plants: number; products: number; activeLocs: number; ingredients: number; openIssues: number; yoshonAnswers: number; waterInfestationAnswers: number };
  banners: Array<{ tone: "warning" | "info" | "danger" | "success"; text: string }>;
}
export interface AddressDto {
  street?: string | null;
  street2?: string | null;
  city?: string | null;
  state?: string | null;
  postalCode?: string | null;
  country?: string | null;
  placeId?: string | null;
  lat?: number | null;
  lng?: number | null;
}
export interface ContactDto {
  id: string;
  name: string;
  email: string | null;
  phone: string | null;
  position: string | null;
  isPrimary: boolean;
  isBilling: boolean;
  hidden: boolean;
  companyId: string | null;
}

export function useRelated<T = unknown>(companyId: string | undefined, list: string, enabled = true) {
  return useQuery({ queryKey: ["company", companyId, "related", list], queryFn: () => api.get<{ items: T[] }>(`/companies/${companyId}/related/${list}`), enabled: !!companyId && enabled });
}

export function useInvalidate() {
  const qc = useQueryClient();
  return (...keys: unknown[][]) => Promise.all(keys.map((k) => qc.invalidateQueries({ queryKey: k })));
}

export function useApiMutation<TIn, TOut>(fn: (input: TIn) => Promise<TOut>, invalidate: unknown[][] = []) {
  const qc = useQueryClient();
  return useMutation({ mutationFn: fn, onSuccess: () => Promise.all(invalidate.map((k) => qc.invalidateQueries({ queryKey: k }))) });
}

export interface AuditItem {
  id: string;
  at: string;
  action: string;
  entityType: string;
  entityId: string | null;
  actorName: string | null;
  summary: string | null;
  reason: string | null;
  prose: string;
}
export function useHistory(entityType: string, entityId: string | undefined) {
  return useInfiniteQuery({
    queryKey: ["audit", entityType, entityId],
    queryFn: ({ pageParam }) => api.get<Page<AuditItem>>(`/audit${qs({ entityType, entityId, cursor: pageParam as string | undefined, limit: 30 })}`),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
    enabled: !!entityId,
  });
}

export interface DashboardDto {
  greetingName: string;
  assignedCompanies: number;
  kpis: { activeLocs: number; locsIssuedThisMonth: number; pendingSubmissions: number; pendingIngredients: number; pendingProducts: number; pendingCompanies: number; inspectionsThisWeek: number; unassignedProductions: number; overdueTotal: number; overdueAccounts: number; failedPayments: number };
  pending: Array<{ kind: "ingredient" | "product" | "company"; id: string; title: string; subtitle: string; status: string; href: string; canApprove: boolean }>;
  expiring: Array<{ kind: "ingredient" | "loc"; id: string; title: string; subtitle: string; days: number; href: string }>;
  upcoming: Array<{ kind: string; id: string; title: string; subtitle: string; when: string | null; tone: "info" | "success" | "warning" | "danger"; status: string | null; href: string }>;
  billing: { failedPayments: Array<{ id: string; companyName: string; amount: number | null; reason: string | null }>; overdue: Array<{ id: string; companyName: string; number: string; total: number | null; daysOverdue: number }>; pendingSyncs: Array<{ companyName: string; count: number }> };
}
export function useDashboard(mine: boolean) {
  return useQuery({ queryKey: ["dashboard", mine], queryFn: () => api.get<DashboardDto>(`/dashboard${qs({ mine: mine ? 1 : undefined })}`) });
}
